import { CommonModule } from '@angular/common';
import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { RouterLink } from '@angular/router';
import {
  INTEL_STATUSES,
  INTEL_TYPES,
  IntelPatch,
  OutreachIntelDto,
  outreachError,
} from '../../../data-transfer-objects/outreach.dto';
import { OutreachService } from '../../../services/outreach.service';
import { PersonLinkComponent } from '../../../components/shared/person-link.component';

interface IntelRow {
  item: OutreachIntelDto;
  owner: string;
  due: string;
  notes: string;
  saving: boolean;
  error: string | null;
}

const OPEN = ['new', 'acknowledged', 'in_progress'];

/**
 * Everything Sharon heard: feedback, ideas, leads, blockers, referrals,
 * praise. Overdue and new first, because those are the ones somebody is
 * waiting on. Status, owner, due date and notes are edited in place; "Propose
 * next step" hands the item to the copilot.
 */
@Component({
  selector: 'app-outreach-intel',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink, MatButtonModule, MatProgressSpinnerModule, PersonLinkComponent],
  templateUrl: './outreach-intel.component.html',
  styleUrls: ['./outreach-intel.component.scss'],
})
export class OutreachIntelComponent implements OnInit {
  @Output() proposeNext = new EventEmitter<string>();

  readonly statuses = INTEL_STATUSES;
  readonly types = INTEL_TYPES;
  status = '';
  type = '';
  rows: IntelRow[] = [];
  loading = true;
  error: string | null = null;

  constructor(private outreach: OutreachService) {}

  ngOnInit(): void {
    this.load();
  }

  setStatus(s: string): void {
    this.status = s;
    this.load();
  }

  setType(t: string): void {
    this.type = t;
    this.load();
  }

  load(): void {
    this.loading = true;
    this.error = null;
    this.outreach.intel({ status: this.status || undefined, type: this.type || undefined }).subscribe({
      next: ({ intel }) => {
        this.loading = false;
        this.rows = sortIntel(intel).map((item) => this.row(item));
      },
      error: (err) => {
        this.loading = false;
        this.error = outreachError(err, 'Could not load intel');
      },
    });
  }

  private row(item: OutreachIntelDto): IntelRow {
    return {
      item,
      owner: item.owner ?? '',
      due: item.dueAt ? item.dueAt.slice(0, 10) : '',
      notes: item.notes ?? '',
      saving: false,
      error: null,
    };
  }

  isOverdue(i: OutreachIntelDto): boolean {
    return OPEN.includes(i.status) && Boolean(i.dueAt) && new Date(i.dueAt!).getTime() < Date.now();
  }

  saveStatus(r: IntelRow, status: string): void {
    if (status !== r.item.status) this.patch(r, { status });
  }

  saveOwner(r: IntelRow): void {
    if (r.owner.trim() !== (r.item.owner ?? '')) this.patch(r, { owner: r.owner.trim() || null });
  }

  saveDue(r: IntelRow): void {
    const current = r.item.dueAt ? r.item.dueAt.slice(0, 10) : '';
    if (r.due !== current) this.patch(r, { dueAt: r.due || null });
  }

  saveNotes(r: IntelRow): void {
    if (r.notes.trim() !== (r.item.notes ?? '')) this.patch(r, { notes: r.notes.trim() || null });
  }

  private patch(r: IntelRow, patch: IntelPatch): void {
    r.saving = true;
    r.error = null;
    this.outreach.updateIntel(r.item.id, patch).subscribe({
      next: ({ intel }) => {
        Object.assign(r, this.row(intel));
      },
      error: (err) => {
        r.saving = false;
        r.error = outreachError(err, 'Not saved');
      },
    });
  }

  propose(i: OutreachIntelDto): void {
    this.proposeNext.emit(`What should we do next with intel #${i.id} ("${i.summary}")?`);
  }
}

/** Overdue first, then new, then the rest; newest first within each. */
export function sortIntel(items: OutreachIntelDto[]): OutreachIntelDto[] {
  const now = Date.now();
  const rank = (i: OutreachIntelDto) =>
    OPEN.includes(i.status) && i.dueAt && new Date(i.dueAt).getTime() < now ? 0 : i.status === 'new' ? 1 : 2;
  return [...items].sort((a, b) => rank(a) - rank(b) || b.createdAt.localeCompare(a.createdAt));
}
